/* eslint-disable @typescript-eslint/no-explicit-any */
import { API_CONFIG } from "@/infrastructure/config/api";
import { UserRole } from "@/shared/types/user-role";
import { getApi, postApi } from "@/shared/utils/fetchApi";

export interface ApiUser {
  id: string;
  email: string;
  role?: string;
  fullName?: string;
  organizationId?: string;
  status?: string;
  avatarUrl?: string;
  isSuperAdmin?: boolean;
  requiresPasswordForDelete?: boolean;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errorCode?: string;
  /** HTTP status da falha — permite distinguir 401 definitivo de erro transiente */
  status?: number;
}

/**
 * Service for handling authentication with JWT tokens stored in localStorage
 */
class AuthService {
  private static instance: AuthService;

  private constructor() {}

  /**
   * Get singleton instance of AuthService
   */
  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public async login(
    email: string,
    password: string,
  ): Promise<
    ApiResponse<{
      user: ApiUser;
      tokens?: { accessToken: string; refreshToken: string; expiresIn: number };
    }>
  > {
    try {
      const url = `${API_CONFIG.API_ROOT_URL}/auth/login`;

      const raw = await postApi<any>(url, { email, password });

      // Normalizar envelope: legacy = { success, data: { user, tokens } }, v2 = { user, tokens }
      const result: ApiResponse<{
        user: ApiUser;
        tokens?: {
          accessToken: string;
          refreshToken: string;
          expiresIn: number;
        };
      }> = { success: true, data: { user: raw.user, tokens: raw.tokens } };

      if (result.success && result.data?.user) {
        if (!result.data.user.role) {
          result.data.user.role = UserRole.USER;
        }

        if (
          result.data.tokens?.accessToken &&
          result.data.tokens?.refreshToken
        ) {
          localStorage.setItem("access_token", result.data.tokens.accessToken);
          localStorage.setItem(
            "refresh_token",
            result.data.tokens.refreshToken,
          );
        }
      }

      return result;
    } catch (error: any) {
      console.error("Login error:", error);
      return {
        success: false,
        message: error.message || "Login failed",
      };
    }
  }

  public async getOAuthUrl(
    provider: "google" | "azure",
    redirectUrl: string,
  ): Promise<ApiResponse<{ url: string }>> {
    try {
      const url = `${API_CONFIG.API_ROOT_URL}/auth/social-sign-in`;

      const raw = await postApi<any>(url, { provider, redirectUrl });

      // Normalizar envelope: v2 = flat { url }, legacy = { success, data: { url } }
      return { success: true, data: { url: raw.url } };
    } catch (error: any) {
      console.error("Get OAuth URL error:", error);
      return {
        success: false,
        message: error.message || "Failed to get OAuth URL",
      };
    }
  }

  public async refreshSession(): Promise<
    ApiResponse<{
      user: ApiUser;
      tokens?: { accessToken: string; refreshToken: string; expiresIn: number };
    }>
  > {
    try {
      const refreshToken = localStorage.getItem("refresh_token");

      const url = `${API_CONFIG.API_ROOT_URL}/auth/refresh`;

      const raw = await postApi<any>(
        url,
        refreshToken ? { refreshToken } : undefined,
      );

      // Normalizar envelope: v2 = flat { user, tokens }, legacy = { success, data: { user, tokens } }
      const result: ApiResponse<{
        user: ApiUser;
        tokens?: {
          accessToken: string;
          refreshToken: string;
          expiresIn: number;
        };
      }> = { success: true, data: { user: raw.user, tokens: raw.tokens } };

      if (
        result.success &&
        result.data?.tokens?.accessToken &&
        result.data?.tokens?.refreshToken
      ) {
        localStorage.setItem("access_token", result.data.tokens.accessToken);
        localStorage.setItem("refresh_token", result.data.tokens.refreshToken);
      }

      return result;
    } catch (error: any) {
      console.error("Refresh session error:", error);
      return {
        success: false,
        message: error.message || "Failed to refresh session",
        status: typeof error.status === "number" ? error.status : undefined,
      };
    }
  }

  public async validateSession(silent: boolean = false): Promise<
    ApiResponse<{
      user: ApiUser;
      tokens?: { accessToken: string; refreshToken: string; expiresIn: number };
    }>
  > {
    try {
      const url = `${API_CONFIG.API_ROOT_URL}/auth/me`;

      const raw = await getApi<any>(url);

      // Normalizar envelope: legacy = { success, data: { user, tokens } }, v2 = User DTO flat
      // (o /auth/me do api retorna o DTO na raiz, sem tokens e sem a chave `user`).
      const result: ApiResponse<{
        user: ApiUser;
        tokens?: {
          accessToken: string;
          refreshToken: string;
          expiresIn: number;
        };
      }> = { success: true, data: { user: raw, tokens: undefined } };

      return result;
    } catch (error: any) {
      if (!silent && error.status !== 401) {
        console.error("Validate session error:", error);
      }

      return {
        success: false,
        message: error.message || "Failed to validate session",
      };
    }
  }

  public async syncOAuthSession(
    accessToken: string,
    refreshToken: string,
    inviteToken?: string | null,
  ): Promise<
    ApiResponse<{
      user: ApiUser;
      needsOnboarding: boolean;
      inviteAccepted?: boolean;
      inviteError?: string;
      inviteErrorCode?: string;
      tokens?: { accessToken: string; refreshToken: string; expiresIn: number };
    }>
  > {
    try {
      const url = `${API_CONFIG.API_ROOT_URL}/auth/oauth/sync`;

      const body: Record<string, string> = { accessToken, refreshToken };
      if (inviteToken) {
        body.inviteToken = inviteToken;
      }

      const raw = await postApi<any>(url, body);

      // `POST /auth/oauth/sync` responde flat. Os campos de convite entram aqui
      // porque a API os manda (`inviteAccepted` sempre, os de erro quando há) e
      // o OAuthCallback usa `inviteErrorCode === "EMAIL_MISMATCH"` para mostrar
      // a tela de e-mail divergente — sem eles essa tela nunca aparecia.
      return {
        success: true,
        data: {
          user: raw.user,
          needsOnboarding: raw.needsOnboarding,
          tokens: raw.tokens,
          inviteAccepted: raw.inviteAccepted,
          inviteError: raw.inviteError,
          inviteErrorCode: raw.inviteErrorCode,
        },
      };
    } catch (error: any) {
      console.error("Sync OAuth session error:", error);
      return {
        success: false,
        message: error.message || "Failed to sync OAuth session",
      };
    }
  }

  public async logout(): Promise<ApiResponse> {
    try {
      const url = `${API_CONFIG.API_ROOT_URL}/auth/logout`;

      const raw = await postApi<any>(url);

      this.clearAllStorageAndCache();

      // Normalizar: v2 = flat { message }, legacy = { success, message }
      return { success: true, message: raw.message };
    } catch (error: any) {
      console.error("Logout error:", error);
      this.clearAllStorageAndCache();
      return {
        success: false,
        message: error.message || "Logout failed",
      };
    }
  }

  /**
   * Clear all storage and cache
   * Used during logout and account deletion to ensure clean state
   */
  private clearAllStorageAndCache(): void {
    try {
      // 1. Limpar localStorage completamente
      localStorage.clear();

      // 2. Limpar sessionStorage completamente
      sessionStorage.clear();

      // 3. Limpar cache do Service Worker (se disponível)
      if ("caches" in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            caches.delete(cacheName);
          });
        });
      }

      // 4. Limpar cookies manualmente (caso backend não limpe)
      document.cookie.split(";").forEach((cookie) => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie =
          name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      });
    } catch (error) {
      console.error("[AuthService] Erro ao limpar storage/cache:", error);
    }
  }
}

export default AuthService;
